import { Button, ButtonProps, Stack } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { clientConfigFieldsAtom } from '../../../../../services/state/AppConfigStateJ';
import { colors } from '../../../../../theme/colors';
import { ActionButtonStack, ActionButtonWrapper } from '../../../../../components/AgTable/gridActionBar';
import { allCompaniesActionStateJ, selectedKpiCompaniesStateDCJ } from '../AllCompaniesStateJ';
import { selectedCompaniesDC } from '../hooks/useAllCompaniesRowSelection';
import { useAllKpiCompaniesActions } from './useAllKpiCompaniesActions';

interface IBulkAssignButtonsProps {
  deselectAll: () => void;
}

export function AllCompaniesBulkActions({ deselectAll }: IBulkAssignButtonsProps) {
  const setSelectedCompanies = useSetAtom(selectedKpiCompaniesStateDCJ);
  const setSelectedAction = useSetAtom(allCompaniesActionStateJ);
  const { handleSelectAction } = useAllKpiCompaniesActions();
  const selected = useAtomValue(selectedCompaniesDC);
  const isConsolidated = !!useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection;

  const handleAssign = useCallback(() => {
    setSelectedCompanies(selected);
    setSelectedAction('assign');
    deselectAll();
  }, [setSelectedCompanies, selected, setSelectedAction, deselectAll]);

  const handleClearTemplates = useCallback(() => {
    setSelectedCompanies(selected);
    setSelectedAction('clearTemplates');
    deselectAll();
  }, [setSelectedCompanies, selected, setSelectedAction, deselectAll]);

  const handleStopReporting = useCallback(() => {
    handleSelectAction(selected, 'confirmStopReporting');
    deselectAll();
  }, [deselectAll, handleSelectAction, selected]);

  const onCancel = useCallback(() => {
    deselectAll();
  }, [deselectAll]);

  if (!selected.length) {
    return null;
  }

  return (
    <Stack display={'grid'} gridTemplateColumns={'auto auto'} justifyContent={'end'} gap='1rem'>
      <ActionButtonWrapper>
        <ActionButtonStack direction={'row'}>
          <ActionButton onClick={handleStopReporting}>Stop Reporting</ActionButton>
          {isConsolidated && <ActionButton onClick={handleClearTemplates}>Clear Templates</ActionButton>}
          <Button onClick={handleAssign} color={'secondary'} variant='contained'>
            Assign
          </Button>
          <Button onClick={onCancel} color={'secondary'} variant='outlined'>
            Cancel
          </Button>
        </ActionButtonStack>
      </ActionButtonWrapper>{' '}
    </Stack>
  );
}

function ActionButton(props: ButtonProps) {
  const { children, ...rest } = props;
  return (
    <Button
      variant='contained'
      color='secondary'
      sx={{ width: 'max-content', borderColor: colors.primary[0] }}
      {...rest}
    >
      {children}
    </Button>
  );
}
